/**
 * Creates an SSR-safe flash message utility backed by a Nuxt cookie.
 *
 * A flash cookie stores ephemeral state that survives a single redirect,
 * navigation, or request cycle, and is automatically cleared once consumed.
 *
 * Unlike regular persistent cookies, flash cookies are intended for
 * one-time delivery semantics.
 *
 * Common use cases include:
 * - success/error notifications after redirects
 * - authentication flow feedback
 * - onboarding or payment completion messages
 * - transient cross-page UI state
 *
 * Internally this composable wraps Nuxt's `useCookie()` and exposes
 * a minimal API for writing and atomically consuming values.
 *
 * @template T Type of the flash payload.
 *
 * @param key Unique cookie key used to persist the flash value.
 *
 * @returns Flash cookie controller with:
 * - `set(value)` — stores a flash payload
 * - `consume()` — retrieves and immediately clears the payload
 *
 * @example
 * ```ts
 * // Set a flash message before redirecting
 * const flash = useFlashCookie('auth-message')
 *
 * flash.set('Signed in successfully')
 *
 * await navigateTo('/dashboard')
 * ```
 *
 * @example
 * ```ts
 * // Consume the flash message on the destination page
 * const flash = useFlashCookie<string>('auth-message')
 *
 * const message = flash.consume()
 *
 * if (message)
 *   toast.success(message)
 * ```
 *
 * @example
 * ```ts
 * // Using structured payloads
 * const flash = useFlashCookie<{
 *   type: 'success' | 'error'
 *   message: string
 * }>('payment-flash')
 *
 * flash.set({
 *   type: 'success',
 *   message: 'Payment completed',
 * })
 * ```
 */
export function useFlashCookie<T = string>(key: string) {
  const cookie = useCookie<T | null>(key)

  /**
   * Stores a flash payload in the cookie.
   *
   * The value remains available until `consume()` is called
   * or the cookie expires naturally.
   *
   * @param value Flash payload to persist.
   */
  const set = (value: T) => {
    cookie.value = value
  }

  /**
   * Retrieves the current flash payload and immediately clears it.
   *
   * This operation is intentionally destructive to guarantee
   * single-consumption semantics.
   *
   * @returns The stored flash value, or `null` if no value exists.
   */
  const consume = (): T | null => {
    if (!cookie.value)
      return null

    const value = cookie.value

    cookie.value = null

    return value
  }

  return {
    set,
    consume,
  }
}
